import { useEffect, useState } from "react";
import DisplayTable, { ColumnTypes, TblColumn } from "../../../components/UI/DisplayTable";
import { ContractEntry, ContractFilters } from "../interfaces/Contracts.interface";
import { getContracts } from "../api/index.api";
import { NavLink } from "react-router-dom";
import { AttachmentDropdown } from "../components/AttachmentsDropdown";
import utils from "../../../auth/data/utils";
import { useAuth } from "../../../context/AuthContext";
import { validateToken } from "../../../login/login";

interface ContractsTableProps {
    filters: ContractFilters;
    openView: (url: string) => void;
}

const ContractsTable:React.FC<ContractsTableProps> = ({filters, openView}) => {
    const [contracts, setContracts] = useState<ContractEntry[]>([]);
    const [totalResults, setTotalResults] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const { userInfo, setUserInfo, login } = useAuth();

    const searchContracts = async(page: number) => {
        setContracts([]);
        setLoading(true);
        const limit = utils().pagination;
        const offset = page * limit;
        const user = await validateToken(userInfo, setUserInfo, login);
        const [total, contracts] = await getContracts(user, filters, limit, offset);
        setContracts(contracts);
        setTotalResults(total);
        setLoading(false);
    }

    const onPaginate = (page: number) => {
        setCurrentPage(page - 1);
        searchContracts(page - 1);
    }

    // Primero hacemos la petición al API
    // TODO: Agregar un abort controller
    useEffect(() => {
        setCurrentPage(0);
        searchContracts(0);

        return () => { };
    }, [filters]);

    const tableColumns:TblColumn[] = [
        {
            columnName: "No.",
            columnType: ColumnTypes.Component,
            allowsSorting: false,
            componentRender: ({ item }) => <DisplayContract item={item} />
        },
        {
            columnName: "Anexo",
            columnType: ColumnTypes.Component,
            allowsSorting: false,
            componentRender: ({ item }) => <DisplayContract item={item} showAnexoNo={true}/>
        },
        {
            columnName: "Marca",
            columnType: ColumnTypes.Text,
            allowsSorting: false,
            dataIndex: "brand"
        },
        {
            columnName: "Modelo",
            columnType: ColumnTypes.Text,
            allowsSorting: false,
            dataIndex: "model"
        },
        {
            columnName: "Versión",
            columnType: ColumnTypes.Text,
            allowsSorting: false,
            dataIndex: "version"
        },
        {
            columnName: "Usuario",
            columnType: ColumnTypes.Text,
            allowsSorting: false,
            dataIndex: "username"
        },
        {
            columnName: "Primera Renta",
            columnType: ColumnTypes.Component,
            allowsSorting: false,
            componentRender: ({item}) => <DisplayDate item={item} index="first_invoice_date"/>
        },
        {
            columnName: "Fecha Vencimiento",
            columnType: ColumnTypes.Component,
            allowsSorting: false,
            componentRender: ({item}) => <DisplayDate item={item} index="due_date"/>
        },
        {
            columnName: "Plazo por Devengar",
            columnType: ColumnTypes.Number,
            allowsSorting: false,
            dataIndex: "plazo_devengar"
        },
        {
            columnName: "Total Mensual",
            columnType: ColumnTypes.Currency,
            allowsSorting: false,
            dataIndex: "monthly_total"
        },
        {
            columnName: "Valor Residual",
            columnType: ColumnTypes.Currency,
            allowsSorting: false,
            dataIndex: "residual_amount"
        },
        {
            columnName: "Adjuntos",
            columnType: ColumnTypes.Component,
            allowsSorting: false,
            componentRender: ({ item }) => <AttachmentDropdown item={item} setFileView={openView}/>
        }
    ];

    return <DisplayTable currentPage={currentPage + 1} tableColumns={tableColumns} tableData={contracts} loading={loading} onPaginate={onPaginate} totalResults={totalResults}/>
};

const DisplayContract:React.FC<{item: ContractEntry, showAnexoNo?: boolean}> = ({item, showAnexoNo = false}) => {
    return(
        <NavLink
            to={`/sumaleasing/Contrato/${item.contract_id}`}
            about="Consultar Contrato"
            className="font-semibold text-primary-700 hover:text-primary-500 transition-colors duration-200"
        >
            {showAnexoNo ? item.anexo_no : item.contract_no}
        </NavLink>
    );
}

const DisplayDate: React.FC<{item: ContractEntry, index: 'first_invoice_date' | 'due_date'}> = ({item, index}) => {

    const dateValue = item[index];

    // 1. Cláusula de Guarda: Si no hay un valor (null, undefined, string vacío), salimos inmediatamente.
    if (!dateValue) {
        return <>-</>;
    }

    // Si llegamos aquí, sabemos que 'dateValue' tiene algo. Ahora lo convertimos.
    const fecha = new Date(dateValue);

    // 2. Verificación de Fecha Inválida: Nos aseguramos de que el string era una fecha real.
    // Si new Date() no puede interpretar el string, crea un objeto "Invalid Date".
    if (isNaN(fecha.getTime())) {
        // Esto previene errores si, por ejemplo, dateValue fuera un string.
        return <>Fecha Inválida</>;
    }

    // 3. Si todo está bien, formateamos y retornamos directamente.
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0');
    const anio = fecha.getFullYear();

    return (
        <>
            {`${mes}-${anio}`}
        </>
    );
}
export default ContractsTable;